import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { videoUrl } from '@/lib/api';
import { ScoreCard } from '@/components/ScoreCard';
import { ScoreRadar } from '@/components/WindCharts';
import { Button } from '@/components/ui/button';
import { Wind, Loader2, Play, ChevronRight, Trophy, AlertTriangle, Clapperboard } from 'lucide-react';
import { Seo } from '@/components/Seo';

const fmt = (s) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, '0')}`; };

export default function SharePage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => { api.get(`/share/${token}`).then(({ data }) => setA(data)).catch(() => setErr(true)); }, [token]);

  const seek = (ts) => { if (videoRef.current) { videoRef.current.currentTime = ts; videoRef.current.play().catch(() => {}); } };

  if (err) return <div className="max-w-2xl mx-auto px-6 py-24 text-center"><Seo noindex title={t('share.notFound')} /><p className="text-xl">{t('share.notFound')}</p><Link to="/" className="text-[#48CAE4] mt-4 inline-block">{t('share.goToKiteiq')}</Link></div>;
  if (!a) return <div className="min-h-[60vh] flex items-center justify-center"><Seo noindex /><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;
  if (a.status !== 'completed') return <div className="max-w-2xl mx-auto px-6 py-24 text-center"><Seo noindex /><p className="text-xl">{t('share.stillAnalyzing')}</p></div>;

  const r = a.result || {};
  const scores = r.scores || {};
  const tricks = r.detected_tricks || [];
  const tips = (r.improvement_tips || []).slice().sort((x, y) => (x.priority || 9) - (y.priority || 9));
  const overall = Object.keys(scores).length ? (Object.values(scores).reduce((p, c) => p + c, 0) / Object.values(scores).length).toFixed(1) : '—';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Seo
        noindex
        title={t('share.seoTitle', {
          level: r.skill_level_detected || t('share.sessionFallback'),
          name: a.owner_name || 'KITEIQ',
        })}
        description={(r.session_summary || t('share.seoDescFallback')).slice(0, 180)}
        image={a.thumbnail || undefined}
        type="article"
      />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 label-eyebrow text-[#48CAE4] mb-2"><Clapperboard className="w-4 h-4" /> {t('share.sharedReport')}</div>
          <h1 className="font-display text-4xl sm:text-5xl capitalize">{r.skill_level_detected || t('share.sessionFallback')} {t('share.sessionWord')}</h1>
          {a.owner_name && <p className="text-muted-foreground mt-1">{t('share.by', { name: a.owner_name })}</p>}
        </div>
        <div className="text-right"><div className="label-eyebrow text-muted-foreground">{t('share.overall')}</div><div className="font-data text-4xl font-semibold text-[#00B4D8]">{overall}<span className="text-base text-muted-foreground">/10</span></div></div>
      </div>

      {r.low_confidence && (
        <div className="mt-6 rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/5 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#FFB020] shrink-0" />
          <div className="text-sm text-muted-foreground">{t('share.lowConfidence')}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="kite-card overflow-hidden">
            {a.video_available ? <video ref={videoRef} src={videoUrl(a.id)} controls poster={a.thumbnail || undefined} playsInline className="w-full aspect-video bg-black" />
              : <div className="w-full aspect-video bg-black flex items-center justify-center text-muted-foreground"><Play className="w-10 h-10" /></div>}
          </div>
          <div className="kite-card p-5"><div className="label-eyebrow text-muted-foreground mb-2">{t('share.summary')}</div><p className="text-lg leading-relaxed">{r.session_summary}</p></div>
          {tricks.length > 0 && (
            <div className="kite-card p-5">
              <h3 className="font-semibold mb-3">{t('share.detectedTricks')}</h3>
              <div className="space-y-2">{tricks.map((tr, i) => (
                <button key={i} onClick={() => seek(tr.timestamp_s)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/8 hover:border-[#48CAE4]/40 text-left">
                  <span className="font-data text-xs px-2 py-1 rounded-md bg-[#00B4D8]/12 text-[#00B4D8]">{fmt(tr.timestamp_s)}</span>
                  <span className="flex-1 capitalize font-medium">{tr.name}</span><span className="font-data text-sm text-[#48CAE4]">{tr.score}/10</span>
                </button>))}</div>
            </div>
          )}
        </div>
        <div className="space-y-6">
          <div className="kite-card p-5"><h3 className="font-semibold mb-2">{t('share.technique')}</h3><ScoreRadar scores={scores} /></div>
          <div className="grid grid-cols-2 gap-3">{Object.entries(scores).map(([k, v]) => <ScoreCard key={k} k={k} value={v} />)}</div>
          <div className="kite-card p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-[#FF6B35]" /> {t('share.topDrills')}</h3>
            <div className="space-y-2">{tips.map((tip, i) => <div key={i} className="flex items-start gap-2 text-sm"><ChevronRight className="w-4 h-4 text-[#48CAE4] mt-0.5 shrink-0" /> {tip.tip}</div>)}</div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-12 rounded-2xl p-8 text-center noise-overlay relative overflow-hidden" style={{ background: 'radial-gradient(60% 100% at 50% 0%, rgba(0,180,216,0.18), rgba(0,180,216,0)), #12161B', border: '1px solid rgba(0,180,216,0.25)' }}>
        <Wind className="w-8 h-8 text-[#00B4D8] mx-auto" />
        <h2 className="font-display text-3xl mt-3">{t('share.ctaTitle')}</h2>
        <p className="text-muted-foreground mt-2">{t('share.ctaSub')}</p>
        <Link to="/register"><Button className="mt-5 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">{t('share.tryFree')}</Button></Link>
      </div>
    </div>
  );
}
